import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Criar instância do axios com configurações padrão
const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
});

// ==========================================================================
// RETRY com backoff exponencial — cobre cold start de backend pós-deploy
// (Kubernetes leva 3-5min pra subir o pod novo). Sem isso, o frontend mostra
// erro imediato ao usuário em vez de aguardar a inicialização do servidor.
// ==========================================================================
const MAX_RETRIES = 4;          // 4 tentativas adicionais
const BASE_DELAY_MS = 1000;      // 1s, 2s, 4s, 8s = ~15s total
const RETRY_STATUSES = [502, 503, 504, 408];  // gateway/cold-start típicos
// NUNCA fazer retry em métodos não-idempotentes EXCETO no login (especial)
const RETRYABLE_METHODS = ['get', 'head', 'options'];
const ALWAYS_RETRY_PATHS = ['/auth/login', '/auth/refresh', '/auth/me'];

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const shouldRetry = (config, error) => {
  if (!config) return false;
  // Já tentou demais
  config.__retryCount = config.__retryCount || 0;
  if (config.__retryCount >= MAX_RETRIES) return false;

  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  const isLoginPath = ALWAYS_RETRY_PATHS.some(p => url.includes(p));
  const methodOk = RETRYABLE_METHODS.includes(method) || isLoginPath;
  if (!methodOk) return false;

  // Erro de rede puro (sem response) — tipo de cold start
  if (!error.response) return true;
  // Status indicando backend ainda subindo
  return RETRY_STATUSES.includes(error.response.status);
};

// Sinaliza pra UI que estamos esperando o backend voltar
let retryToastId = null;
const showRetryingToast = (attempt) => {
  if (retryToastId) toast.dismiss(retryToastId);
  retryToastId = toast.loading(
    `Servidor inicializando, aguarde... (${attempt}/${MAX_RETRIES})`,
    { id: 'server-warmup' }
  );
};
const dismissRetryToast = () => {
  if (retryToastId) {
    toast.dismiss(retryToastId);
    retryToastId = null;
  }
};

// Interceptor de requisição - adiciona token automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nautilus_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de resposta - 401 + retry exponencial em cold-start
let isRedirecting = false;
api.interceptors.response.use(
  (response) => {
    dismissRetryToast();  // sucesso → some o "aguarde"
    return response;
  },
  async (error) => {
    const config = error.config;

    // Retry em cold-start
    if (shouldRetry(config, error)) {
      config.__retryCount += 1;
      const delay = BASE_DELAY_MS * Math.pow(2, config.__retryCount - 1);
      showRetryingToast(config.__retryCount);
      await sleep(delay);
      return api(config);
    }
    dismissRetryToast();

    // 401 (Unauthorized) → sessão expirada/inválida
    if (error.response && error.response.status === 401 && !isRedirecting) {
      isRedirecting = true;
      localStorage.removeItem('nautilus_token');
      localStorage.removeItem('nautilus_user');
      toast.error('Sessão expirada. Faça login novamente.');
      setTimeout(() => {
        window.location.href = '/login';
        isRedirecting = false;
      }, 500);
    }

    // Erro de rede/timeout depois de todas as tentativas
    if (error.code === 'ECONNABORTED' || !error.response) {
      toast.error('Servidor indisponível. Tente novamente em instantes.');
    }

    return Promise.reject(error);
  }
);

// Helper para obter headers de autenticação (para componentes que usam axios direto)
export const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// Verificar se o token está válido
export const isTokenValid = () => {
  const token = localStorage.getItem('nautilus_token');
  if (!token) return false;
  
  try {
    // Decodificar o JWT para verificar expiração
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Converter para milliseconds
    return Date.now() < exp;
  } catch (e) {
    return false;
  }
};

// Auto-refresh: renova token quando faltam menos de 10 min para expirar
let refreshTimer = null;
export const startTokenRefresh = () => {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(async () => {
    const token = localStorage.getItem('nautilus_token');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresIn = (payload.exp * 1000) - Date.now();
      // Renovar se faltar menos de 10 minutos
      if (expiresIn > 0 && expiresIn < 10 * 60 * 1000) {
        const res = await axios.post(`${API_URL}/api/auth/refresh`, null, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        });
        if (res.data?.access_token) {
          localStorage.setItem('nautilus_token', res.data.access_token);
        }
      }
    } catch (e) {
      // Silently ignore refresh errors
    }
  }, 60000); // Check every minute
};

export const stopTokenRefresh = () => {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
};

// Fazer logout (chama backend para revogar token antes de limpar local)
export const logout = async () => {
  const token = localStorage.getItem('nautilus_token');
  if (token) {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, null, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
    } catch (e) {
      // Even if backend call fails, proceed with local logout
    }
  }
  localStorage.removeItem('nautilus_token');
  localStorage.removeItem('nautilus_user');
  window.location.href = '/login';
};

export default api;
